import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog as D } from 'radix-ui';
import { FolderOpen, HardDrive, History, Server } from 'lucide-react';
import { useOpenedStore } from '../db/context';
import { pickBackup, pickFolder, restoreBackup, setDataDir } from '../db/backup';
import { serverConfigured } from '../sync/config';
import { useUi } from '../state/ui';

const KEY = 'nexus-open-welcomed';

function shouldShow(): boolean {
  try {
    if (localStorage.getItem(KEY)) return false;
  } catch {
    return false;
  }
  // Test builds skip it, unless a test asks for it with ?welcome.
  return import.meta.env.VITE_SKIP_WELCOME !== 'true' || location.search.includes('welcome');
}

function remember() {
  try {
    localStorage.setItem(KEY, '1');
  } catch {
    // Private window: it shows again next time, nothing more.
  }
}

/** First start: how do you want to use Nexus Open? Everything can be changed later. */
export function Welcome() {
  const { t } = useTranslation();
  const opened = useOpenedStore();
  const desktop = opened.backend === 'desktop';
  const [open, setOpen] = useState(shouldShow);
  const [error, setError] = useState<string>();

  const close = () => {
    remember();
    setOpen(false);
  };
  const attempt = async (work: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await work();
    } catch (e) {
      const msg = String(e instanceof Error ? e.message : e);
      setError(msg === 'invalid' ? t('data.invalidBackup') : msg);
    }
  };

  const choice =
    'flex w-full items-start gap-3 rounded-xl border border-border p-3 text-left hover:bg-bg-hover';
  return (
    <D.Root open={open} onOpenChange={(o) => !o && close()}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <D.Content className="fixed top-1/2 left-1/2 z-50 max-h-[90dvh] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-bg p-6 shadow-pop">
          <D.Title className="text-xl font-semibold">{t('welcome.title')}</D.Title>
          <D.Description className="mt-1 text-sm text-fg-muted">{t('welcome.intro')}</D.Description>
          <div className="mt-5 space-y-2">
            <button type="button" className={choice} onClick={close}>
              <HardDrive className="mt-0.5 size-5 shrink-0 text-accent" />
              <span>
                <span className="block font-medium">{t('welcome.local')}</span>
                <span className="block text-xs text-fg-muted">
                  {t(desktop ? 'welcome.localHintDesktop' : 'welcome.localHint')}
                </span>
              </span>
            </button>
            {desktop && (
              <button
                type="button"
                className={choice}
                onClick={() =>
                  void attempt(async () => {
                    const dir = await pickFolder();
                    if (!dir) return;
                    remember();
                    await setDataDir(dir, 'open');
                  })
                }
              >
                <FolderOpen className="mt-0.5 size-5 shrink-0 text-accent" />
                <span>
                  <span className="block font-medium">{t('welcome.folder')}</span>
                  <span className="block text-xs text-fg-muted">{t('welcome.folderHint')}</span>
                </span>
              </button>
            )}
            {(desktop || opened.driver.importDb) && (
              <button
                type="button"
                className={choice}
                onClick={() =>
                  void attempt(async () => {
                    const src = await pickBackup(opened);
                    if (!src) return;
                    remember();
                    await restoreBackup(opened, src);
                  })
                }
              >
                <History className="mt-0.5 size-5 shrink-0 text-accent" />
                <span>
                  <span className="block font-medium">{t('welcome.restore')}</span>
                  <span className="block text-xs text-fg-muted">{t('welcome.restoreHint')}</span>
                </span>
              </button>
            )}
            <button
              type="button"
              className={choice}
              onClick={() => {
                close();
                useUi.getState().set({ settingsOpen: true, settingsTab: 'account' });
              }}
            >
              <Server className="mt-0.5 size-5 shrink-0 text-accent" />
              <span>
                <span className="block font-medium">{t('welcome.server')}</span>
                <span className="block text-xs text-fg-muted">
                  {t(serverConfigured() ? 'welcome.serverHint' : 'welcome.serverLater')}
                </span>
              </span>
            </button>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          )}
          <p className="mt-4 text-xs text-fg-muted">{t('welcome.later')}</p>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
